import { memo, useCallback } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { yupResolver } from '@hookform/resolvers/yup';
import NextImage from 'next/image';

import { SignInFormWrapper } from './styles';
import Title from 'components/common/title';
import Text from 'components/common/text';
import TextField from 'components/common/text-field';
import { Fields } from 'types/fields';
import { Button } from 'components/common/button';
import { PageLoader } from 'components/common/loader';
import { useSignIn } from 'lib/mutation/sign-in';
import { useAppDispatch } from 'hooks/useRedux';
import { addUser } from 'store/slices/auth';
import TextFieldPassword from 'components/common/text-field-password';
import { signInSchema } from 'utils/validations';
import { AuthContent } from 'components/layout/auth-layout/styles';
import { useHandleWithGoogle } from 'lib/mutation/google';
import Error from 'components/common/error';
import { FORGOT_PASSWORD_ROUTE, HOME_ROUTE } from 'constants/routes';

function SignInForm() {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { isError, isLoading, signIn, error } = useSignIn();
  const {
    error: errGoogle,
    isError: isErrGoogle,
    loading,
    handleGoogle,
  } = useHandleWithGoogle({});

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Fields>({
    mode: 'onChange',
    resolver: yupResolver(signInSchema),
  });

  const onSubmit: SubmitHandler<Fields> = async (data) => {
    try {
      const { user, jwt } = await signIn({
        identifier: data.signInEmail,
        password: data.signInPassword,
      });

      dispatch(
        addUser({ data: user, jwt: { ...jwt, access: `Bearer ${jwt.access}` } })
      );

      router.replace(HOME_ROUTE);
    } catch {
      console.error('Unknown Err');
    }
  };

  const signWithGoogle = useCallback(() => {
    handleGoogle();
  }, [handleGoogle]);

  return (
    <AuthContent>
      <SignInFormWrapper>
        <div className="sign-in-head">
          <Title fw="bold" type="mode1" className="sign-in-head-title">
            Sign In to Richr
          </Title>
          <Text fw="light" className="sign-in-head-subtitle">
            Enter your details below
          </Text>

          {isError && <Error data={error?.response?.data.errors} />}
          {isErrGoogle && <Error data={errGoogle?.response?.data.errors} />}
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="sign-in-form">
          <TextField
            type="text"
            name="signInEmail"
            label="Email"
            register={register}
            error={errors.signInEmail}
            placeholder="Enter your email"
          />
          <TextFieldPassword
            type="password"
            name="signInPassword"
            label="Password"
            register={register}
            error={errors.signInPassword}
            placeholder="Enter your password"
          />

          <div className="sign-in-forgot">
            <NextLink href={FORGOT_PASSWORD_ROUTE} passHref>
              <a>
                <Text fw="light">Forgot password?</Text>
              </a>
            </NextLink>
          </div>
          <div className="sign-in-form-submit">
            <Button type="submit">
              <Text type="mode3" textTransform="uppercase">
                Sign In
              </Text>
            </Button>
          </div>
        </form>
        <Text className="or-line">
          <span />
          or
          <span />
        </Text>

        <div className="sign-in-google">
          <Button
            mode="google"
            className="sign-in-form-google"
            onClick={signWithGoogle}
          >
            <NextImage src="/static/icons/google.svg" width={24} height={24} />

            <Text>Continue with Google</Text>
          </Button>
        </div>

        {(isLoading || loading) && <PageLoader />}
      </SignInFormWrapper>
    </AuthContent>
  );
}

export default memo(SignInForm);
